import type { AsyncState } from "@react-hookz/web";
import { useAsync, useMountEffect, useSyncedRef } from "@react-hookz/web";
import axios from "axios";
import getUnixTime from "date-fns/getUnixTime";
import isEmpty from "lodash/isEmpty";
import React, { useEffect, useMemo } from "react";
import { fetchQuery, graphql, useRelayEnvironment } from "react-relay";
import { useControlledPromise } from "scmp-sdk";
import { useSdkContext } from "scmp-sdk/components/contexts";
import type { TooltipItem } from "scmp-sdk/components/dashboard-widget/tooltips";

import { useFallbackStorageValue } from "shared/hooks";

import type { hooksUseGroupSubscriptionDataQuery } from "scmp-app/queries/__generated__/hooksUseGroupSubscriptionDataQuery.graphql";

import { fetchCorporateSubscriberTier } from "./apis";
import { GroupSubscriberTooltip } from "./component";
import type {
  AcquisitionAppConfig,
  CorporateSubscriberResponse,
  GroupSubscriberTooltipData,
} from "./types";

export const useGroupSubscriberTooltipRender = () => {
  const { user } = useSdkContext();

  const {
    remove: removeGroupSubscriberData,
    set: setGroupSubscriberData,
    value: groupSubscriberData,
  } = useFallbackStorageValue<GroupSubscriberTooltipData>("groupSubscriberTooltipData");

  const acquisitionConfigState = useAsyncAcquisitionConfig();
  const corporateTierState = useAsyncHasCorporateSubscriberTier();
  const syncedGroupSubscriberData = useSyncedRef(groupSubscriberData);

  const { promise, reject, resolve } = useControlledPromise<TooltipItem>();

  useEffect(() => {
    if (isPromiseIncomplete(acquisitionConfigState) || isPromiseIncomplete(corporateTierState))
      return;
    else if (
      !user ||
      isPromiseWithError(acquisitionConfigState) ||
      isPromiseWithError(corporateTierState)
    )
      return reject.current();

    const acquisitionConfigStateResult = acquisitionConfigState.result;
    const corporateTierStateResult = corporateTierState.result;

    if (!acquisitionConfigStateResult || !corporateTierStateResult) return reject.current();

    if (syncedGroupSubscriberData.current?.version !== acquisitionConfigStateResult.tooltip.version)
      removeGroupSubscriberData();

    const currentGroupSubscriberData = syncedGroupSubscriberData.current ?? {
      intervalData: [
        {
          intervalTime: 0,
          shown: false,
        },
        {
          intervalTime: 604_800,
          shown: false,
        },
        {
          intervalTime: 1_209_600,
          shown: false,
        },
        {
          intervalTime: 1_814_400,
          shown: false,
        },
        {
          intervalTime: 2_419_200,
          shown: false,
        },
      ],
      lastShownTimeStamp: getUnixTime(Date.now()),
      version: acquisitionConfigStateResult.tooltip.version,
    };

    const secondsSinceLastShownTimestamp =
      getUnixTime(Date.now()) - currentGroupSubscriberData.lastShownTimeStamp;

    if (!currentGroupSubscriberData.intervalData) return reject.current();

    for (const [index] of currentGroupSubscriberData.intervalData.entries()) {
      const validIntervalData = currentGroupSubscriberData.intervalData;

      const updateGroupSubscriberData = () => {
        setGroupSubscriberData(() => {
          const clonedIntervals = [...validIntervalData];
          clonedIntervals[index] = {
            intervalTime: validIntervalData[index].intervalTime,
            shown: true,
          };
          return {
            intervalData: clonedIntervals,
            lastShownTimeStamp: currentGroupSubscriberData.lastShownTimeStamp,
            version: currentGroupSubscriberData.version ?? "",
          };
        });
      };

      if (
        secondsSinceLastShownTimestamp >=
          currentGroupSubscriberData.intervalData[index].intervalTime &&
        !currentGroupSubscriberData.intervalData[index].shown
      ) {
        return resolve.current({
          node: (
            <GroupSubscriberTooltip
              message={`${acquisitionConfigStateResult.tooltip.message}`}
              onImpression={() => {
                updateGroupSubscriberData();
              }}
            />
          ),
          onChangeUrl: () => {
            updateGroupSubscriberData();
          },
        });
      }
    }

    return reject.current();
  }, [
    acquisitionConfigState,
    corporateTierState,
    reject,
    removeGroupSubscriberData,
    resolve,
    setGroupSubscriberData,
    syncedGroupSubscriberData,
    user,
    promise,
  ]);

  return {
    groupSubscriberTooltipRender: promise,
  };
};

const isPromiseIncomplete = <T,>(state: AsyncState<T>) =>
  !state || state.status === "loading" || state.status === "not-executed";

const isPromiseWithError = <T,>(state: AsyncState<T>) => state.status === "error";

const useAsyncAcquisitionConfig = () => {
  const environment = useRelayEnvironment();
  const { config } = useSdkContext();

  const [state, action] = useAsync(async () => {
    const response = await fetchQuery<hooksUseGroupSubscriptionDataQuery>(
      environment,
      graphql`
        query hooksUseGroupSubscriptionDataQuery {
          appConfig(filter: { entityId: "acquisition" }) {
            json
          }
        }
      `,
      {},
    ).toPromise();

    if (response?.appConfig?.json)
      return (response?.appConfig?.json as AcquisitionAppConfig)[config.environment];

    return;
  });

  useMountEffect(() => {
    void action.execute();
  });

  return state;
};

const useAsyncHasCorporateSubscriberTier = () => {
  const { config, user } = useSdkContext();

  const {
    remove: removeCorporateSubscriberTier,
    set: setCorporateSubscriberTier,
    value: corporateSubscriberTier,
  } = useFallbackStorageValue<CorporateSubscriberResponse>("corp_subscriber_tier");

  const axiosInstance = useMemo(() => {
    const instance = axios.create({
      baseURL: config.corporateSubscriber.host,
      headers: { apikey: config.graphql.apiKey },
    });
    return instance;
  }, [config]);

  // This state contains tells whether user belongs to the tiers below
  const [state, action] = useAsync(async () => {
    const tiers = new Set(["C-suite", "T2", "T3", "T4"]);

    if (!user?.useremail) return false;

    if (
      corporateSubscriberTier &&
      corporateSubscriberTier?.["Users Email Address"] !== user?.useremail
    )
      removeCorporateSubscriberTier();

    if (!isEmpty(corporateSubscriberTier)) return tiers.has(corporateSubscriberTier?.Tier ?? "");

    const data = await fetchCorporateSubscriberTier(axiosInstance, user?.useremail);

    if (isEmpty(corporateSubscriberTier) && data) setCorporateSubscriberTier(data);

    return tiers.has(data?.Tier ?? "");
  });

  useMountEffect(() => void action.execute());

  return state;
};
